import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  TrendingUp, CheckCircle2, Clock, AlertCircle, Search, DollarSign,
  Plus, X, Loader2, Eye, FileText, RefreshCw, Calendar, Building2,
  CreditCard, Banknote, AlertTriangle, Trash2, RotateCcw, FileSpreadsheet, Sparkles, Pencil
} from "lucide-react";
import axios from "axios";
import { exportarReporte, fechaReporte } from "../utils/reportes";
import EstadoCuentaModal from "../components/EstadoCuentaModal";   // Fix FO (2026-10-09)
import EstadoCuentaTab from "./cobranzas/EstadoCuentaTab";   // Fix GZ (2026-10-19)
import { API, useAuth } from "../contexts/AuthContext";
import { formatPYG, formatPYGCompact, formatDate, formatUSD2, todayAsuncion } from "../utils/format";
import { useTipoCambio, fmtTC } from "../utils/tipoCambio";   // Fix FG (2026-10-08)
import { Skeleton } from "../components/ui/skeleton";
import SelectBuscable from "../components/SelectBuscable";   // Fix HG (2026-10-20)
import { opcionesObrasPpto } from "../utils/obras";   // Fix HG (2026-10-20) · Fix HR (2026-10-23)
import { toast } from "sonner";

const STATUS_MAP = {
  pendiente: { label: "Por Cobrar",  cls: "bg-amber-100 text-amber-700 border-amber-200" },
  cobrado:   { label: "Cobrado",     cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  parcial:   { label: "Parcial",     cls: "bg-blue-100 text-blue-700 border-blue-200" },
  vencido:   { label: "Vencido",     cls: "bg-red-100 text-red-700 border-red-200" },
  cancelado: { label: "Cancelado",   cls: "bg-gray-100 text-gray-500 border-gray-200" },
  anulada:   { label: "Anulada",     cls: "bg-gray-200 text-gray-600 border-gray-300" },   // Fix HO (2026-10-22)
};

const PAYMENT_METHODS = ["transferencia", "efectivo", "cheque", "débito directo", "otro"];
// Fix GL (2026-10-16): retención de IVA (resumen técnico de CEN): se calcula sobre el IVA, al 30, 70 o
// 100 %. Jornal mínimo diario vigente desde el 01/07/2026 (Decreto 6225/26): por debajo de 10 jornales
// la ley no exige retener (solo se avisa). Actualizar el valor cuando cambie el salario mínimo.
const RETENCION_PCTS = [30, 70, 100];
const JORNAL_MINIMO_DIARIO = 117077;
const RX_RETENCION = /^\d{3}-\d{3}-\d{7}$/;

// Fix EA (2026-09-23): "para registrar el cobro, se debe solicitar obligatoriamente cargar
// el nro de comprobante y un screenshot o imagen del comprobante bancario. También se debe
// cargar el N° de factura emitida para el cobro". Mismos campos en "Registrar Cobro" y en
// el cobro de un hito; el backend los exige igual.
const COBRO_ANULAR_ROLES = ["Superadministrador", "Administrador", "Gerencia Financiera", "Contador"];
const RECIBO_ROLES = ["Superadministrador", "Administrador", "Contador", "Gerente Tecnico", "Gerencia Financiera"];   // Fix HU (2026-10-23)

function datosCobroIniciales(cob) {
  return { comprobante_numero: "", comprobante_nombre: "", comprobante_base64: "",
    nro_factura: cob?.nro_factura || cob?.invoice_number || "" };
}

function faltanDatosCobro(d) {
  if (!d.comprobante_numero.trim()) return "Cargá el N° de comprobante";
  if (!d.comprobante_base64) return "Adjuntá la imagen o el PDF del comprobante bancario";
  if (!d.nro_factura.trim()) return "Cargá el N° de la factura emitida";
  return "";
}

function CamposComprobanteCobro({ datos, setDatos, retencion = false, onArchivo, children }) {   // Fix GL: retencion = comprobante de retención de IVA · Fix GP: onArchivo + panel de la IA
  const elegirArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setDatos(p => ({ ...p, comprobante_nombre: "", comprobante_base64: "" })); onArchivo?.(null); return; }
    if (!(file.type.startsWith("image/") || file.type === "application/pdf")) {
      toast.error("El comprobante tiene que ser una imagen o un PDF"); e.target.value = ""; return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error("El comprobante no puede superar 5 MB"); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setDatos(p => ({ ...p, comprobante_nombre: file.name, comprobante_base64: ev.target.result })); onArchivo?.(ev.target.result); };
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-3 bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid="cobro-comprobante-campos">
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">{retencion ? "N° del comprobante de retención *" : "N° de comprobante *"}</label>
        <input value={datos.comprobante_numero} onChange={e => setDatos(p => ({ ...p, comprobante_numero: e.target.value }))}
          placeholder={retencion ? "000-000-0000000" : "N° de transferencia, depósito o cheque"} className={`w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white ${retencion ? "font-mono" : ""}`}
          data-testid="cobro-comprobante-numero" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">{retencion ? "Comprobante de retención (PDF o imagen) *" : "Comprobante bancario (imagen o PDF) *"}</label>
        <input type="file" accept="image/*,.pdf" onChange={elegirArchivo} className="block w-full text-xs text-gray-600"
          data-testid="cobro-comprobante-archivo" />
        {datos.comprobante_nombre && <p className="text-xs text-emerald-700 mt-1">{datos.comprobante_nombre}</p>}
        {children}
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">N° de factura emitida *</label>
        <input value={datos.nro_factura} onChange={e => setDatos(p => ({ ...p, nro_factura: e.target.value }))}
          placeholder="001-001-0000000" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white font-mono"
          data-testid="cobro-nro-factura" />
      </div>
    </div>
  );
}

// Fix GP (2026-10-16): "sería bueno que se suba el comprobante de la retención del IVA y que la IA lo lea,
// a modo que haga un autocompletado o control cruzado" · "lo mismo aplicable al comprobante bancario, que la
// IA lea el monto cobrado y lo compare con la factura, etc. Caso el monto no corresponda que dé una alerta"
// (CEN). Al adjuntar el comprobante, la IA lo lee: completa los campos y lo cruza con la cobranza. Si hay
// alertas, el cobro se guarda solo después de confirmarlas; el servidor repite el control al confirmar.
function useLecturaIA(cob, { retencion = false, hitoId = null, aplicar }) {
  const [archivo, setArchivo] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  const [res, setRes] = useState(null);            // { lectura, alertas, autocompletar } o { error }
  const [alertas, setAlertas] = useState([]);
  const [confirmadas, setConfirmadas] = useState(false);
  const turno = useRef(0);
  const aplicarRef = useRef(aplicar);
  aplicarRef.current = aplicar;
  const leer = useCallback(async (dataUrl, esRet) => {
    const n = ++turno.current;
    setRes(null); setAlertas([]); setConfirmadas(false);
    if (!dataUrl) { setLeyendo(false); return; }
    setLeyendo(true);
    try {
      const { data } = await axios.post(`${API}/treasury/cobranzas/${cob._id}/leer-comprobante`,
        { comprobante_base64: dataUrl, es_retencion: esRet, hito_id: hitoId }, { withCredentials: true });
      if (n !== turno.current) return;
      setRes(data); setAlertas(data.alertas || []);
      aplicarRef.current?.(data.autocompletar || {});
    } catch (err) {
      if (n !== turno.current) return;
      setRes({ error: typeof err.response?.data?.detail === "string" ? err.response.data.detail : "No se pudo leer el comprobante con IA: completá los datos a mano" });
    } finally {
      if (n === turno.current) setLeyendo(false);
    }
  }, [cob._id, hitoId]);
  useEffect(() => { if (archivo) leer(archivo, retencion); }, [retencion]);   // eslint-disable-line react-hooks/exhaustive-deps
  return {
    leyendo, res, alertas, confirmadas, setConfirmadas,
    onArchivo: (dataUrl) => { setArchivo(dataUrl); leer(dataUrl, retencion); },
    payload: () => ({ lectura_ia: res?.lectura || null, alertas_confirmadas: confirmadas ? alertas : [] }),
    bloquea: leyendo || (alertas.length > 0 && !confirmadas),
    // 409 del servidor al confirmar: las alertas con lo cargado (hay que volver a confirmarlas)
    errorAlConfirmar: (err) => {
      const det = err.response?.data?.detail;
      if (err.response?.status === 409 && det && Array.isArray(det.alertas)) {
        setAlertas(det.alertas); setConfirmadas(false);
        toast.error(det.mensaje || "Revisá las alertas del comprobante");
        return true;
      }
      return false;
    },
  };
}

function PanelLecturaIA({ ia, retencion = false, usd = false }) {
  if (!ia.leyendo && !ia.res && ia.alertas.length === 0) return null;
  const f = v => usd ? formatUSD2(v || 0) : formatPYG(v || 0);
  const l = ia.res?.lectura;
  return (
    <div className="mt-2 space-y-2" data-testid="cobro-ia-panel">
      {ia.leyendo && (
        <p className="flex items-center gap-1.5 text-xs text-sky-700 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2" data-testid="cobro-ia-leyendo">
          <Loader2 size={13} className="animate-spin" /> Leyendo el comprobante con IA…
        </p>
      )}
      {ia.res?.error && (
        <p className="text-xs text-gray-600 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2" data-testid="cobro-ia-error">
          {ia.res.error}. El cobro queda «sin lectura IA».
        </p>
      )}
      {l && (
        <div className="text-xs bg-white border border-sky-100 rounded-lg px-3 py-2 text-gray-700" data-testid="cobro-ia-leido">
          <p className="flex items-center gap-1 font-semibold text-sky-800 mb-0.5"><Sparkles size={12} /> Leído por IA (confianza {l.confianza} %)</p>
          {retencion ? (
            <p>Retenido {f(l.total_retenido)}{l.porcentaje ? ` · ${l.porcentaje} % de ${f(l.base || l.iva)}` : ""} · N° {l.numero || "—"} · {l.fecha ? formatDate(l.fecha) : "sin fecha"}
              {l.factura ? ` · Factura ${l.factura}` : ""}{l.agente ? ` · ${l.agente}` : ""}</p>
          ) : (
            <p>{l.monto ? (l.moneda === "USD" ? formatUSD2(l.monto) : formatPYG(l.monto)) : "Monto sin leer"} · N° {l.numero || "—"} · {l.fecha ? formatDate(l.fecha) : "sin fecha"}
              {l.banco ? ` · ${l.banco}` : ""}{l.ordenante ? ` · Pagó: ${l.ordenante}` : ""}</p>
          )}
        </div>
      )}
      {l && ia.alertas.length === 0 && (
        <p className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="cobro-ia-ok">
          <CheckCircle2 size={13} /> El comprobante coincide con la cobranza
        </p>
      )}
      {ia.alertas.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1" data-testid="cobro-ia-alertas">
          <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={13} /> Revisá antes de confirmar</p>
          <ul className="list-disc pl-4 space-y-0.5">{ia.alertas.map((a, i) => <li key={i}>{a}</li>)}</ul>
          <label className="flex items-start gap-2 pt-1 cursor-pointer font-semibold">
            <input type="checkbox" checked={ia.confirmadas} onChange={e => ia.setConfirmadas(e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-600" data-testid="cobro-ia-confirmar" />
            Revisé estas alertas y confirmo el cobro igual
          </label>
        </div>
      )}
    </div>
  );
}

// Fix GP: resultado del control con IA guardado en cada comprobante del cobro.
function ControlIABadge({ c }) {
  if (!c) return null;
  const est = {
    verificado: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Verificado con IA"],
    con_alertas: ["bg-amber-50 text-amber-700 border-amber-200", "Con alertas confirmadas"],
    sin_lectura: ["bg-gray-50 text-gray-500 border-gray-200", "Sin lectura IA"],
  }[c.estado] || ["bg-gray-50 text-gray-500 border-gray-200", c.estado];
  return <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${est[0]}`} data-testid="cobro-control-ia">{est[1]}</span>;
}

function StatCard({ title, value, sub, color = "gray", icon: Icon }) {
  const colors = {
    blue:    "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber:   "bg-amber-50 text-amber-600 border-amber-100",
    red:     "bg-red-50 text-red-600 border-red-100",
    gray:    "bg-gray-50 text-gray-600 border-gray-100",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        <div className={`p-2 rounded-lg border ${colors[color]}`}><Icon size={15} /></div>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// Auditoría 2026-08-27, CAMBIO 4 — ahora se genera en el backend con
// reportlab, leyendo los datos de empresa desde Configuración.
async function downloadCobranzaPdf(cob) {
  try {
    const res = await axios.get(`${API}/treasury/cobranzas/${cob._id}/export-pdf`, {
      withCredentials: true, responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${cob.number}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error("Error al generar el PDF de la cobranza");
  }
}

// Fix HU (2026-10-23): "A partir de este módulo, debe generar el recibo para entregar al cliente, las facturas crédito"
// (CEN, Claudio). Descarga el Recibo de Dinero (PDF, original y duplicado) de un cobro; devuelve su N° o "" si no se pudo.
async function descargarRecibo(cobId, compId) {
  try {
    const res = await axios.post(`${API}/treasury/cobranzas/${cobId}/comprobantes/${compId}/recibo`, {}, { withCredentials: true, responseType: "blob" });
    const numero = res.headers["x-recibo-numero"] || "";
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Recibo_${numero}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return numero;
  } catch (err) {
    let msg = "No se pudo generar el recibo";
    try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch { /* sin detalle */ }
    toast.error(msg);
    return "";
  }
}
// Fix HS (2026-10-23): "será que pueden hacer que quede ordenado por número lo ya cargado también, las
// nuevas cargas ya lo realizan" (CEN, Claudio). La lista sale ordenada por N° de factura, de la más
// nueva a la más vieja (igual que quedan las cargas nuevas); las que no tienen N° van al final.
function claveFactura(c) {
  const txt = String(c?.nro_factura || c?.invoice_number || "").trim();
  const grupos = txt.match(/\d+/g);
  if (!grupos) return null;
  return { n: parseInt(grupos[grupos.length - 1], 10), serie: grupos.slice(0, -1).join("-") };
}
function porNumeroDesc(a, b) {
  const ka = claveFactura(a), kb = claveFactura(b);
  if (!ka || !kb) return ka ? -1 : kb ? 1 : 0;
  return (kb.n - ka.n) || kb.serie.localeCompare(ka.serie);
}
// ── Nueva Factura Modal ───────────────────────────────────────────────────────
// Fix GY (2026-10-19): "Nueva Factura no nueva cobranza" · "Desplegable con datos de clientes en base
// de datos del sistema" · "Condición de venta: contado, crédito" · "Debe controlar correlatividad de
// número de orden de las facturas y correlatividad de fecha" · "Una vez terminada la carga debe tener
// también un icono de modificar" (CEN, Claudio). Con `cob` el mismo formulario modifica la factura.
function NuevaFacturaModal({ projects, cob, onClose, onSaved }) {
  const editando = !!cob;
  const [clientes, setClientes] = useState([]);
  const [ultima, setUltima] = useState(null);
  const [salto, setSalto] = useState("");
  const [anulada, setAnulada] = useState(false);   // Fix HO (2026-10-22): factura anulada (solo número y fecha)
  const saltoRef = useRef(null);
  useEffect(() => { if (salto) saltoRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [salto]);   // el aviso queda a la vista
  const [form, setForm] = useState(() => cob ? {
    client_name: cob.client_name || "", cliente_id: cob.cliente_id || "", ruc: cob.ruc || "", description: cob.description || "",
    amount: String(cob.moneda === "USD" ? cob.monto_usd || "" : cob.amount || ""), project_id: cob.project_id || "",
    invoice_number: cob.nro_factura || cob.invoice_number || "", issue_date: String(cob.fecha_factura || cob.issue_date || todayAsuncion()).slice(0, 10),
    due_date: String(cob.due_date || "").slice(0, 10), payment_method: cob.payment_method || "transferencia", notes: cob.notes || "",
    has_iva: !!cob.has_iva, moneda: cob.moneda === "USD" ? "USD" : "PYG", tipo_cambio: cob.moneda === "USD" ? String(cob.tipo_cambio || "") : "",
    retencion_porcentaje: String(cob.retencion_porcentaje || 0),
    timbrado: cob.timbrado || "", venc_timbrado: cob.venc_timbrado || "", condicion: cob.condicion || "",
  } : {
    client_name: "", cliente_id: "", ruc: "", description: "", amount: "", project_id: "",
    invoice_number: "", issue_date: todayAsuncion(),
    due_date: "", payment_method: "transferencia", notes: "", has_iva: true,
    moneda: "PYG", tipo_cambio: "",   // Fix FG (2026-10-08): "también debemos poder cobrar en USD" (CEN)
    retencion_porcentaje: "0",   // Fix GL (2026-10-16)
    timbrado: "", venc_timbrado: "", condicion: "",   // Fix GY
  });
  const [saving, setSaving] = useState(false);
  const cambiar = (campo, valor) => { setSalto(""); setForm(p => ({ ...p, [campo]: valor })); };
  const tcDia = useTipoCambio(form.issue_date);
  const usd = form.moneda === "USD";
  const tcUsado = parseFloat(form.tipo_cambio) || tcDia?.usd_pyg || 0;
  // Fix GL: retención = IVA × porcentaje; lo que el cliente paga en efectivo = total − retención.
  const montoNum = parseFloat(form.amount) || 0;
  const conRetencion = form.has_iva && !usd;
  const retPct = conRetencion ? Number(form.retencion_porcentaje) || 0 : 0;
  const retMonto = Math.round(Math.round(montoNum / 11) * retPct / 100);
  // Fix GY: con cobros o certificación, el monto, el IVA y la moneda no se cambian acá.
  const bloqueaMonto = editando && (!!cob.certificacion_id || (cob.amount_paid || 0) > 0
    || (cob.comprobantes_cobro || []).some(x => !x.anulado) || (cob.hitos || []).length > 0);

  useEffect(() => {
    axios.get(`${API}/listas/clientes`, { withCredentials: true }).then(({ data }) => {
      const act = (data || []).filter(c => (c.estado || "activo") !== "inactivo");
      setClientes(act);
      if (cob && !cob.cliente_id) {   // facturas viejas: el cliente era texto libre; se busca por nombre
        const m = act.find(c => (c.cliente || "").trim().toLowerCase() === (cob.client_name || "").trim().toLowerCase());
        if (m) setForm(p => ({ ...p, cliente_id: m._id, ruc: p.ruc || m.ruc_ci || "" }));
      }
    }).catch(() => toast.error("No se pudo cargar la lista de clientes"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarUltima = (timbrado, nro, prefill) => {
    const m = /^\s*(\d{3}-\d{3})-/.exec(nro || "");
    const qs = new URLSearchParams({ ...(timbrado ? { timbrado } : {}), ...(m ? { prefijo: m[1] } : {}) }).toString();
    axios.get(`${API}/treasury/facturas-venta/ultima${qs ? `?${qs}` : ""}`, { withCredentials: true }).then(({ data }) => {
      setUltima(data);
      if (prefill) setForm(p => ({ ...p, timbrado: p.timbrado || data.timbrado || "", venc_timbrado: p.venc_timbrado || data.venc_timbrado || "",
        invoice_number: p.invoice_number || data.siguiente || "" }));
    }).catch(() => setUltima(null));
  };
  useEffect(() => { cargarUltima(editando ? cob.timbrado : "", editando ? cob.nro_factura || cob.invoice_number : "", !editando); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const elegirCliente = (id) => {
    setSalto("");
    if (id === "__texto") return;
    const c = clientes.find(x => x._id === id);
    setForm(p => ({ ...p, cliente_id: id, client_name: c ? c.cliente : "", ruc: c ? c.ruc_ci || "" : "" }));
  };
  const clienteElegido = clientes.find(x => x._id === form.cliente_id);
  const sinFiscal = editando && !form.invoice_number.trim() && !form.timbrado.trim() && !form.condicion;

  const handleSave = async (confirmarSalto = false) => {
    // Fix HO (2026-10-22): "también debe permitir cargar facturas anuladas para que quede el historial de todo el
    // movimiento de la facturación" (CEN, Claudio). La anulada lleva solo timbrado, número y fecha.
    if (anulada) {
      if (!/^\d+$/.test(form.timbrado.trim())) { toast.error("Cargá el timbrado (solo números)"); return; }
      if (!/^\d{3}-\d{3}-\d{1,7}$/.test(form.invoice_number.trim())) { toast.error("El N° de factura va como 001-001-0000001"); return; }
    } else if (!form.client_name || !form.description || !form.amount) {
      toast.error("Complete: Cliente, Descripción y Monto");
      return;
    }
    if (!sinFiscal && !anulada) {
      if (!form.ruc.trim()) { toast.error("Cargá el RUC o C.I. del cliente"); return; }
      if (!form.condicion) { toast.error("Elegí la condición de venta: contado o crédito"); return; }
      if (!/^\d+$/.test(form.timbrado.trim())) { toast.error("Cargá el timbrado (solo números)"); return; }
      if (!/^\d{3}-\d{3}-\d{1,7}$/.test(form.invoice_number.trim())) { toast.error("El N° de factura va como 001-001-0000001"); return; }
      if (form.condicion === "credito" && !form.due_date) { toast.error("Indicá la fecha de vencimiento de la factura a crédito"); return; }
    }
    if (usd && !tcUsado && !anulada) { toast.error("Cargá el tipo de cambio"); return; }
    setSaving(true);
    try {
      const body = anulada ? { anulada: true, client_name: "ANULADA", description: "FACTURA ANULADA", amount: 0, invoice_number: form.invoice_number,
        timbrado: form.timbrado, venc_timbrado: form.venc_timbrado, issue_date: form.issue_date, notes: form.notes, confirmar_salto: confirmarSalto }   // Fix HO
        : { ...form, amount: parseFloat(form.amount), tipo_cambio: usd ? tcUsado : null, retencion_porcentaje: retPct,   // Fix GL
        confirmar_salto: confirmarSalto };
      if (bloqueaMonto) ["amount", "moneda", "tipo_cambio", "has_iva", "retencion_porcentaje"].forEach(k => delete body[k]);
      if (editando) await axios.put(`${API}/treasury/cobranzas/${cob._id}`, body, { withCredentials: true });
      else await axios.post(`${API}/treasury/cobranzas`, body, { withCredentials: true });
      toast.success(editando ? "Factura modificada correctamente" : anulada ? "Factura anulada registrada" : "Factura registrada correctamente");
      onSaved();
      onClose();
    } catch (err) {
      const det = err.response?.data?.detail;
      if (err.response?.status === 409 && det && det.codigo === "salto") setSalto(det.mensaje);
      else toast.error(typeof det === "string" ? det : (editando ? "Error al modificar la factura" : "Error al registrar la factura"), { duration: 15000 });   // Fix HX: el aviso de fechas lista facturas y orden
    } finally {
      setSaving(false);
    }
  };

  const campo = "w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all disabled:bg-gray-50 disabled:text-gray-400";
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">{editando ? `Modificar factura ${cob.nro_factura || cob.invoice_number || cob.number}` : "Nueva Factura"}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Fix HO (2026-10-22): factura anulada: ocupa su número y su fecha en la serie, sin cliente ni monto. */}
          {!editando && (
            <label htmlFor="cob-anulada-check" className="flex items-start gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 cursor-pointer">
              <input type="checkbox" id="cob-anulada-check" checked={anulada} onChange={e => { setSalto(""); setAnulada(e.target.checked); }} className="w-4 h-4 mt-0.5 accent-emerald-600" data-testid="cob-anulada-check" />
              <span className="text-xs text-gray-700"><b>Factura anulada</b> — ocupa su número y queda en el Libro de Ventas, sin cliente ni monto.</span>
            </label>
          )}
          {anulada && (
            <div className="grid grid-cols-2 gap-4" data-testid="cob-anulada-bloque">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Timbrado *</label>
                <input value={form.timbrado} onChange={e => cambiar("timbrado", e.target.value.replace(/\D/g, ""))} onBlur={() => cargarUltima(form.timbrado, form.invoice_number, false)}
                  placeholder="12345678" className={campo} data-testid="cob-anu-timbrado" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Vencimiento del timbrado</label>
                <input type="date" value={form.venc_timbrado} onChange={e => cambiar("venc_timbrado", e.target.value)} className={`${campo} bg-white`} data-testid="cob-anu-venc-timbrado" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">N° Factura *</label>
                <input value={form.invoice_number} onChange={e => cambiar("invoice_number", e.target.value)} onBlur={() => cargarUltima(form.timbrado, form.invoice_number, false)}
                  placeholder="001-001-0000001" className={campo} data-testid="cob-anu-invoice" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha *</label>
                <input type="date" value={form.issue_date} onChange={e => cambiar("issue_date", e.target.value)} className={`${campo} bg-white`} data-testid="cob-anu-issue" />
              </div>
              {ultima && (ultima.ultimo || ultima.siguiente) && (
                <p className="col-span-2 text-[11px] text-gray-500 -mt-2" data-testid="cob-anu-ultima-hint">
                  {ultima.ultimo ? <>Última cargada: <b>{ultima.ultimo}</b> ({formatDate(ultima.ultima_fecha)}) · </> : "Todavía no hay facturas cargadas en esta serie · "}
                  Sigue: <b>{ultima.siguiente}</b>.
                </p>
              )}
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Motivo / Observaciones</label>
                <textarea value={form.notes} onChange={e => cambiar("notes", e.target.value)} rows={2} placeholder="Ej.: error de impresión, se rehízo con otro número..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" data-testid="cob-anu-notes" />
              </div>
            </div>
          )}
          {!anulada && (
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Cliente *</label>
              <select value={form.cliente_id || (form.client_name ? "__texto" : "")} onChange={e => elegirCliente(e.target.value)} className={`${campo} bg-white`} data-testid="cob-cliente-select">
                <option value="">Elegí un cliente...</option>
                {!form.cliente_id && form.client_name && <option value="__texto">{form.client_name} (cargado antes como texto)</option>}
                {clientes.map(c => <option key={c._id} value={c._id}>{c.cliente}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">RUC / C.I. del cliente *</label>
              <input value={form.ruc} onChange={e => cambiar("ruc", e.target.value)} placeholder="80000000-0" className={campo} data-testid="cob-ruc-input" />
              {clienteElegido && !clienteElegido.ruc_ci && <p className="text-[11px] text-amber-700 mt-1">Este cliente no tiene RUC en la lista de clientes: escribilo acá.</p>}
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Descripción / Concepto *</label>
              <input value={form.description} onChange={e => cambiar("description", e.target.value)} placeholder="Servicio, obra, entregable..." className={campo} data-testid="cob-description-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Timbrado *</label>
              <input value={form.timbrado} onChange={e => cambiar("timbrado", e.target.value.replace(/\D/g, ""))} onBlur={() => !editando && cargarUltima(form.timbrado, form.invoice_number, false)}
                placeholder="12345678" className={campo} data-testid="cob-timbrado-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Vencimiento del timbrado</label>
              <input type="date" value={form.venc_timbrado} onChange={e => cambiar("venc_timbrado", e.target.value)} className={`${campo} bg-white`} data-testid="cob-venc-timbrado-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">N° Factura *</label>
              <input value={form.invoice_number} onChange={e => cambiar("invoice_number", e.target.value)} onBlur={() => !editando && cargarUltima(form.timbrado, form.invoice_number, false)}
                placeholder="001-001-0000001" className={campo} data-testid="cob-invoice-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha Emisión *</label>
              <input type="date" value={form.issue_date} onChange={e => cambiar("issue_date", e.target.value)} className={`${campo} bg-white`} data-testid="cob-issue-input" />
            </div>
            {ultima && (ultima.ultimo || ultima.siguiente) && (
              <p className="col-span-2 text-[11px] text-gray-500 -mt-2" data-testid="cob-ultima-hint">
                {ultima.ultimo ? <>Última cargada: <b>{ultima.ultimo}</b> ({formatDate(ultima.ultima_fecha)}) · </> : "Todavía no hay facturas cargadas en esta serie · "}
                Sigue: <b>{ultima.siguiente}</b>. Los números y las fechas van en orden.
              </p>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Condición de venta *</label>
              <select value={form.condicion} onChange={e => cambiar("condicion", e.target.value)} className={`${campo} bg-white`} data-testid="cob-condicion-select">
                <option value="">Elegí...</option>
                <option value="contado">Contado</option>
                <option value="credito">Crédito</option>
              </select>
            </div>
            <div>
              {form.condicion === "credito" ? (<>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha Vencimiento *</label>
                <input type="date" value={form.due_date} onChange={e => cambiar("due_date", e.target.value)} className={`${campo} bg-white`} data-testid="cob-due-input" />
              </>) : form.condicion === "contado" ? (
                <p className="text-[11px] text-gray-500 pt-6" data-testid="cob-contado-nota">Contado: vence el día de la factura.</p>
              ) : <div />}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Moneda</label>
              <select value={form.moneda} onChange={e => cambiar("moneda", e.target.value)} disabled={bloqueaMonto} className={`${campo} bg-white`} data-testid="cob-moneda-select">
                <option value="PYG">Guaraníes (PYG)</option>
                <option value="USD">Dólares (USD)</option>
              </select>
            </div>
            <div>
              {usd ? (<>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tipo de cambio (Gs. por USD)</label>
                <input type="number" value={form.tipo_cambio} onChange={e => cambiar("tipo_cambio", e.target.value)} disabled={bloqueaMonto} placeholder={tcDia ? String(tcDia.usd_pyg) : ""} className={campo} data-testid="cob-tc-input" />
                {tcDia && <p className="text-[10px] text-gray-400 mt-0.5">Del día: {fmtTC(tcDia.usd_pyg)} · {tcDia.fuente}</p>}
              </>) : <div />}
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Proyecto</label>
              {/* Fix HG (2026-10-20): obras de la A a la Z y con buscador (se puede escribir el nombre).
                  Fix HR (2026-10-23): "NOMBRE DE OBRA - ÚLTIMOS 3 DÍGITOS DEL PRESUPUESTO - MONTO TOTAL C/ IVA" (CEN, Claudio);
                  el campo ocupa el ancho completo para que se lea entero. */}
              <SelectBuscable value={form.project_id} onChange={v => cambiar("project_id", v)}
                options={opcionesObrasPpto(projects)} vacio="Sin proyecto..." placeholderBuscar="Buscar obra, cliente o N° de presupuesto..."
                className={`${campo} bg-white`} testId="cob-project-select" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Monto ({usd ? "USD" : "PYG"}) *</label>
              <input type="number" value={form.amount} onChange={e => cambiar("amount", e.target.value)} disabled={bloqueaMonto} placeholder="0" className={campo} data-testid="cob-amount-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Método de Pago</label>
              <select value={form.payment_method} onChange={e => cambiar("payment_method", e.target.value)} className={`${campo} bg-white`}>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
              </select>
            </div>
            {bloqueaMonto && (
              <p className="col-span-2 text-[11px] text-amber-700 -mt-2" data-testid="cob-monto-bloqueado">
                {cob.certificacion_id ? "Esta venta viene de una certificación: el monto se cambia en la certificación." : "Ya tiene cobros registrados: para cambiar el monto, el IVA o la moneda, anulá primero el cobro."}
              </p>
            )}
            <div className="col-span-2 flex items-center gap-3">
              <input type="checkbox" id="cob-iva" checked={form.has_iva} disabled={bloqueaMonto} onChange={e => cambiar("has_iva", e.target.checked)} className="w-4 h-4 accent-emerald-600" />
              <label htmlFor="cob-iva" className="text-xs text-gray-600 cursor-pointer">Incluye IVA (10%) — se desglosa automáticamente</label>
            </div>
            {/* Fix GL (2026-10-16): "Las retenciones de IVA se deben cargar desde COBRANZAS al generar
                NUEVA COBRANZA" (CEN). La retención se calcula sobre el IVA. */}
            {conRetencion && (
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Retención de IVA</label>
                <select value={form.retencion_porcentaje} onChange={e => cambiar("retencion_porcentaje", e.target.value)} disabled={bloqueaMonto} className={`${campo} bg-white`} data-testid="cob-retencion-select">
                  <option value="0">Sin retención</option>
                  {RETENCION_PCTS.map(p => <option key={p} value={String(p)}>{p} % del IVA</option>)}
                </select>
                {retPct > 0 && montoNum > 0 && (
                  <p className="text-xs text-violet-700 mt-1" data-testid="cob-retencion-resumen">
                    Retención: Gs. {retMonto.toLocaleString("es-PY")} · A cobrar en efectivo: Gs. {Math.round(montoNum - retMonto).toLocaleString("es-PY")}
                  </p>
                )}
                {retPct > 0 && montoNum > 0 && montoNum < 10 * JORNAL_MINIMO_DIARIO && (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="cob-retencion-umbral">
                    La factura no llega a 10 jornales mínimos (Gs. {(10 * JORNAL_MINIMO_DIARIO).toLocaleString("es-PY")}): por ley no corresponde retener IVA por debajo de ese monto.
                  </p>
                )}
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
              <textarea value={form.notes} onChange={e => cambiar("notes", e.target.value)} rows={2} placeholder="Notas adicionales..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" />
            </div>
          </div>
          )}
          {usd && form.amount > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700 flex justify-between flex-wrap gap-2" data-testid="cob-usd-preview">
              {form.has_iva && <span>Neto: {formatUSD2(parseFloat(form.amount) - parseFloat(form.amount) / 11)}</span>}
              {form.has_iva && <span>IVA 10%: {formatUSD2(parseFloat(form.amount) / 11)}</span>}
              <span className="font-bold">Total: {formatUSD2(form.amount)} ≈ {formatPYGCompact(Math.round(parseFloat(form.amount) * tcUsado))} Gs</span>
            </div>
          )}
          {!usd && form.amount > 0 && form.has_iva && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-xs text-emerald-700 flex justify-between">
              <span>Monto neto: {formatPYGCompact(parseFloat(form.amount || 0) - Math.round(parseFloat(form.amount || 0) / 11))} Gs</span>
              <span>IVA 10%: {formatPYGCompact(Math.round(parseFloat(form.amount || 0) / 11))} Gs</span>
              <span className="font-bold">Total: {formatPYGCompact(parseFloat(form.amount || 0))} Gs</span>
            </div>
          )}
          {salto && (
            <div ref={saltoRef} className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 space-y-2" data-testid="cob-salto-aviso">
              <p>{salto}</p>
              <div className="flex gap-2">
                <button onClick={() => setSalto("")} className="px-3 py-1.5 rounded-lg border border-amber-300 font-semibold hover:bg-amber-100">Revisar el número</button>
                <button onClick={() => handleSave(true)} disabled={saving} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-60" data-testid="cob-salto-confirmar">Confirmar y registrar</button>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={() => handleSave(false)} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="create-cobranza-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : editando ? <Pencil size={15} /> : <Plus size={15} />}
              {saving ? "Guardando..." : editando ? "Guardar cambios" : anulada ? "Registrar factura anulada" : "Registrar factura"}
            </button>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}

// Fix EB (2026-09-23): Excel y PDF de lo que muestra la tabla, con los filtros aplicados.
function BotonesExportar({ exportar, exportando, testId }) {
  return (
    <div className="flex gap-2">
      <button onClick={() => exportar("excel")} disabled={!!exportando}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
        data-testid={`${testId}-excel`}>
        {exportando === "excel" ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />} Excel
      </button>
      <button onClick={() => exportar("pdf")} disabled={!!exportando}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        data-testid={`${testId}-pdf`}>
        {exportando === "pdf" ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} PDF
      </button>
      {/* Fix FO (2026-10-09): ver el reporte en pantalla, sin descargarlo */}
      <button onClick={() => exportar("ver")} disabled={!!exportando}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        data-testid={`${testId}-preview`}>
        <Eye size={13} /> Vista previa
      </button>
    </div>
  );
}

// ── Marcar Cobrado Modal ──────────────────────────────────────────────────────
function MarkPaidModal({ cob, onClose, onSaved }) {
  const usd = cob.moneda === "USD";   // Fix FG (2026-10-08): cobro en USD
  // Fix GL (2026-10-16): la factura se cobra con lo que paga el cliente en efectivo más el comprobante
  // de retención de IVA, que suele llegar después. Se propone primero el efectivo.
  const ivaCob = Number(cob.iva_amount || 0) || ((cob.has_iva || cob.certificacion_id) ? Math.round(Number(cob.amount || 0) / 11) : 0);
  const retCobrada = Number(cob.retencion_cobrada || 0);
  const retPendiente = Math.max(Number(cob.retencion_iva || 0) - retCobrada, 0);
  const saldoGs = Math.max(Number(cob.amount || 0) - Number(cob.amount_paid || 0), 0);
  const efectivoPendiente = Math.max(saldoGs - retPendiente, 0);
  const admiteRetencion = !usd && ivaCob > 0;
  const retSugerida = (pct) => retPendiente || Math.max(Math.round(ivaCob * pct / 100) - retCobrada, 0);
  const [form, setForm] = useState({
    // Fix GE (2026-10-15): en Gs. el campo es el monto de ESTE cobro y propone lo que falta cobrar.
    amount_paid: String(usd ? cob.monto_usd : (efectivoPendiente || saldoGs)),   // Fix GL: sin la retención por cobrar
    tipo_cambio: "",
    payment_method: PAYMENT_METHODS.includes(cob.payment_method) ? cob.payment_method : "transferencia",   // Fix GL
    payment_date: new Date().toISOString().slice(0, 10),
    notes: "",
    es_retencion: false, retencion_porcentaje: String(cob.retencion_porcentaje || 30), retencion_cdc: "",   // Fix GL
  });
  const setRetencion = (on) => setForm(p => ({ ...p, es_retencion: on,   // Fix GL
    amount_paid: String(on ? retSugerida(Number(p.retencion_porcentaje) || 30) : (efectivoPendiente || saldoGs)) }));
  const [datos, setDatos] = useState(() => datosCobroIniciales(cob));
  const [saving, setSaving] = useState(false);
  const tcDia = useTipoCambio(form.payment_date, usd);
  const tcCobro = parseFloat(form.tipo_cambio) || tcDia?.usd_pyg || 0;
  const total = usd ? Number(cob.monto_usd || 0) : Number(cob.amount || 0);
  const yaCobrado = usd ? Number(cob.monto_usd_cobrado || 0) : Number(cob.amount_paid || 0);
  const fmt = v => usd ? formatUSD2(v) : `${formatPYGCompact(v)} Gs`;
  // Fix GE (2026-10-15): "al cargar una cobranza parcial, por ej 517.452 gs, el ERP arroja un resultado
  // equivocado: lo cobrado debería ser 517.452 gs y el saldo restante 14.508 gs" (CEN). El servidor
  // recibe el TOTAL cobrado: antes de este arreglo, un segundo cobro de 14.508 dejaba lo cobrado en
  // 14.508 y la cobranza en Parcial. En Gs. el campo es el monto de este cobro y se suma a lo ya
  // cobrado; en USD sigue siendo el total cobrado.
  const montoIngresado = parseFloat(form.amount_paid) || 0;
  const totalCobrado = usd ? montoIngresado : yaCobrado + montoIngresado;
  // Fix GP (2026-10-16): la IA lee el comprobante adjunto y completa monto, N°, fecha, % y CDC.
  const ia = useLecturaIA(cob, { retencion: form.es_retencion, aplicar: (a) => {
    setForm(p => ({ ...p,
      ...(a.monto ? { amount_paid: String(usd ? Math.round((yaCobrado + Number(a.monto)) * 100) / 100 : Math.round(Number(a.monto))) } : {}),
      ...(a.fecha ? { payment_date: a.fecha } : {}),
      ...(a.metodo && !p.es_retencion ? { payment_method: a.metodo } : {}),
      ...(a.porcentaje ? { retencion_porcentaje: String(a.porcentaje) } : {}),
      ...(a.cdc ? { retencion_cdc: a.cdc } : {}),
    }));
    if (a.numero) setDatos(p => ({ ...p, comprobante_numero: a.numero }));
  } });

  const handleSubmit = async () => {
    const falta = faltanDatosCobro(datos);
    if (falta) { toast.error(falta); return; }
    if (ia.bloquea) { toast.error(ia.leyendo ? "Esperá a que termine la lectura del comprobante" : "Revisá y confirmá las alertas del comprobante"); return; }   // Fix GP
    if (!usd && montoIngresado <= 0) { toast.error("Ingresá el monto de este cobro"); return; }
    if (form.es_retencion && !RX_RETENCION.test(datos.comprobante_numero.trim())) {   // Fix GL
      toast.error("El N° del comprobante de retención va con el formato 000-000-0000000"); return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/treasury/cobranzas/${cob._id}/mark-paid`, {
        ...form, ...datos, amount_paid: usd ? parseFloat(form.amount_paid) : totalCobrado, tipo_cambio: usd ? tcCobro : null,
        es_retencion: form.es_retencion, retencion_porcentaje: form.es_retencion ? Number(form.retencion_porcentaje) : null,   // Fix GL
        retencion_cdc: form.es_retencion ? form.retencion_cdc : "",
        ...ia.payload(),   // Fix GP: lectura de la IA y alertas confirmadas
      }, { withCredentials: true });
      toast.success(form.es_retencion ? "Retención de IVA registrada" : totalCobrado >= total ? "Cobro registrado correctamente" : "Cobro parcial registrado");
      onSaved();
      onClose();
    } catch (err) {
      if (!ia.errorAlConfirmar(err)) toast.error(err.response?.data?.detail || "Error al registrar cobro");   // Fix GP
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Registrar Cobro</h2>
            <p className="text-xs text-gray-500 mt-0.5">{cob.number} · {cob.client_name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700">
            Monto total a cobrar: <span className="font-bold">{fmt(total)}</span>
            {yaCobrado > 0 && <span> · Ya cobrado: {fmt(yaCobrado)} · Saldo: {fmt(Math.max(total - yaCobrado, 0))}</span>}
            {/* Fix GL: lo que paga el cliente en efectivo y la retención que llega después */}
            {!usd && Number(cob.retencion_iva || 0) > 0 && (
              <span className="block mt-1 text-violet-700" data-testid="cobro-resumen-retencion">
                Efectivo por cobrar: <span className="font-bold">{formatPYG(efectivoPendiente)}</span> · Retención de IVA por cobrar: <span className="font-bold">{formatPYG(retPendiente)}</span>
              </span>
            )}
          </div>
          {/* Fix GL (2026-10-16): el comprobante de retención de IVA se registra como un cobro aparte */}
          {admiteRetencion && (
            <label className="flex items-center gap-2 text-xs text-violet-800 bg-violet-50 border border-violet-100 rounded-xl p-3 cursor-pointer">
              <input type="checkbox" checked={form.es_retencion} onChange={e => setRetencion(e.target.checked)} className="w-4 h-4 accent-violet-600" data-testid="cobro-es-retencion" />
              Este cobro es el comprobante de retención de IVA del cliente
            </label>
          )}
          {form.es_retencion && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Porcentaje retenido</label>
                <select value={form.retencion_porcentaje} onChange={e => { const v = e.target.value; setForm(p => ({ ...p, retencion_porcentaje: v, amount_paid: String(retSugerida(Number(v))) })); }}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" data-testid="cobro-retencion-pct">
                  {RETENCION_PCTS.map(p => <option key={p} value={String(p)}>{p} % del IVA</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">CDC (si es electrónico)</label>
                <input value={form.retencion_cdc} onChange={e => setForm(p => ({ ...p, retencion_cdc: e.target.value }))} maxLength={44} placeholder="44 dígitos"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white font-mono" data-testid="cobro-retencion-cdc" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">{usd ? "Total cobrado (USD) *" : form.es_retencion ? "Monto retenido (Gs) *" : "Monto de este cobro (Gs) *"}</label>
            <input type="number" value={form.amount_paid} onChange={e => setForm(p => ({ ...p, amount_paid: e.target.value }))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-200" data-testid="paid-amount-input" />
            {totalCobrado < total && montoIngresado > 0 && (
              <p className="text-xs text-amber-600 mt-1">Cobro parcial: quedan {fmt(total - totalCobrado)} pendientes</p>
            )}
          </div>
          {usd && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tipo de cambio del cobro (Gs. por USD)</label>
              <input type="number" value={form.tipo_cambio} onChange={e => setForm(p => ({ ...p, tipo_cambio: e.target.value }))} placeholder={tcDia ? String(tcDia.usd_pyg) : ""} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-200" data-testid="paid-tc-input" />
              <p className="text-[11px] text-gray-500 mt-1" data-testid="paid-usd-equivalente">
                {tcDia && <>Del día del cobro: {fmtTC(tcDia.usd_pyg)} · {tcDia.fuente}. </>}
                Este cobro: {formatUSD2(Math.max(parseFloat(form.amount_paid || 0) - yaCobrado, 0))} ≈ {formatPYGCompact(Math.round(Math.max(parseFloat(form.amount_paid || 0) - yaCobrado, 0) * tcCobro))} Gs
              </p>
            </div>
          )}
          {!form.es_retencion && (   /* Fix GL: la retención va como «retención de IVA» */
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Método de Pago</label>
            <select value={form.payment_method} onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-emerald-200">
              {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
            </select>
          </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">{form.es_retencion ? "Fecha de la retención" : "Fecha de Pago"}</label>
            <input type="date" value={form.payment_date} onChange={e => setForm(p => ({ ...p, payment_date: e.target.value }))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-emerald-200" />
          </div>
          <CamposComprobanteCobro datos={datos} setDatos={setDatos} retencion={form.es_retencion} onArchivo={ia.onArchivo}>
            <PanelLecturaIA ia={ia} retencion={form.es_retencion} usd={usd} />{/* Fix GP */}
          </CamposComprobanteCobro>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Número de transferencia, cheque, etc." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={handleSubmit} disabled={saving || !!faltanDatosCobro(datos) || ia.bloquea} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="confirm-paid-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {saving ? "Guardando..." : "Confirmar Cobro"}
            </button>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
function DetailModal({ cob, onClose, onMarkPaid, onSaved }) {
  const st = STATUS_MAP[cob.anulada ? "anulada" : cob.status] || { label: cob.status, cls: "" };   // Fix HO
  // Fix EA (2026-09-23): comprobantes del cobro y "Anular cobro" (reemplaza al "Revertir
  // pago" que había en Obra → Certificaciones). Las certificaciones anteriores a EA
  // guardaron el IVA en 0: se muestra el 10% incluido.
  const { user } = useAuth();
  const puedeAnular = COBRO_ANULAR_ROLES.includes(user?.role) && (cob.status === "cobrado" || cob.status === "parcial");
  const [confirmarAnular, setConfirmarAnular] = useState(false);
  const [anulando, setAnulando] = useState(false);
  // Fix HU (2026-10-23): recibo de dinero de cada cobro (facturas que no son de contado).
  const [numeros, setNumeros] = useState({});
  const [emitiendo, setEmitiendo] = useState("");
  const [primerRecibo, setPrimerRecibo] = useState(null);   // { cp, valor }: falta definir desde qué N° sigue el talonario
  const puedeRecibo = RECIBO_ROLES.includes(user?.role) && cob.condicion !== "contado" && !cob.anulada;
  const emitirRecibo = async (cp) => {
    setEmitiendo(cp.id);
    const n = await descargarRecibo(cob._id, cp.id);
    if (n) { setNumeros(p => ({ ...p, [cp.id]: n })); onSaved && onSaved(); }
    setEmitiendo("");
  };
  const pedirRecibo = async (cp) => {
    if (!cp.recibo_numero && !numeros[cp.id]) {
      try {
        const { data } = await axios.get(`${API}/treasury/recibos/numeracion`, { withCredentials: true });
        if (!data.configurada) { setPrimerRecibo({ cp, valor: String(data.proximo_numero) }); return; }
      } catch { /* si no se pudo consultar, decide el servidor al emitir */ }
    }
    emitirRecibo(cp);
  };
  const guardarPrimerRecibo = async () => {
    try {
      await axios.put(`${API}/treasury/recibos/numeracion`, { proximo_numero: parseInt(primerRecibo.valor, 10) || 0 }, { withCredentials: true });
      const cp = primerRecibo.cp;
      setPrimerRecibo(null);
      emitirRecibo(cp);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar el N° inicial"); }
  };
  const comprobantes = cob.comprobantes_cobro || [];
  const iva = cob.iva_amount || ((cob.has_iva || cob.certificacion_id) ? Math.round((cob.amount || 0) / 11) : 0);
  const anular = async () => {
    setAnulando(true);
    try {
      await axios.put(`${API}/treasury/cobranzas/${cob._id}/anular-cobro`, {}, { withCredentials: true });
      toast.success("Cobro anulado: la cobranza vuelve a Por Cobrar");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo anular el cobro");
    } finally { setAnulando(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <p className="text-xs text-gray-500 font-mono">{cob.number}</p>
            <h2 className="text-lg font-bold text-gray-900">{cob.client_name}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-gray-400">Estado</p><span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${st.cls}`}>{st.label}</span></div>
            <div><p className="text-xs text-gray-400">Proyecto</p><p className="font-medium">{cob.project_name || "—"}</p></div>
            <div><p className="text-xs text-gray-400">Descripción</p><p className="font-medium">{cob.description}</p></div>
            <div><p className="text-xs text-gray-400">N° Factura</p><p className="font-medium font-mono">{cob.nro_factura || cob.invoice_number || "—"}</p></div>
            <div><p className="text-xs text-gray-400">Fecha Emisión</p><p className="font-medium">{cob.issue_date || "—"}</p></div>
            <div><p className="text-xs text-gray-400">Fecha Vencimiento</p><p className="font-medium">{cob.due_date || "—"}</p></div>
          </div>
          {cob.moneda === "USD" && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 grid grid-cols-3 gap-4 text-center" data-testid="cob-detalle-usd">
              <div><p className="text-xs text-blue-400">Monto (USD)</p><p className="text-base font-bold text-blue-900">{formatUSD2(cob.monto_usd)}</p></div>
              <div><p className="text-xs text-blue-400">Cobrado (USD)</p><p className="text-base font-bold text-blue-900">{formatUSD2(cob.monto_usd_cobrado)}</p></div>
              <div><p className="text-xs text-blue-400">Cambio de emisión</p><p className="text-sm font-semibold text-blue-900">{fmtTC(cob.tipo_cambio)}</p></div>
              {(cob.cobros_usd || []).map((x, i) => (
                <p key={i} className="col-span-3 text-[11px] text-blue-700 text-left">Cobro del {x.fecha}: {formatUSD2(x.usd)} × {Math.round(x.tipo_cambio).toLocaleString("es-PY")} = {formatPYGCompact(x.pyg)} Gs</p>
              ))}
            </div>
          )}
          <div className={`bg-gray-50 rounded-xl p-4 grid ${cob.moneda === "USD" ? "grid-cols-3" : "grid-cols-4"} gap-4 text-center`}>
            <div><p className="text-xs text-gray-400">Monto Total{cob.moneda === "USD" ? " (Gs. al cambio de emisión)" : ""}</p><p className="text-base font-bold text-gray-900">{formatPYGCompact(cob.amount)} Gs</p></div>
            <div><p className="text-xs text-gray-400">IVA (10%)</p><p className="text-base font-bold text-gray-700">{formatPYGCompact(iva)} Gs</p></div>
            <div><p className="text-xs text-gray-400">Cobrado</p><p className={`text-base font-bold ${cob.amount_paid >= cob.amount ? "text-emerald-600" : "text-amber-600"}`}>{formatPYGCompact(cob.amount_paid || 0)} Gs</p></div>
            {/* Fix GE (2026-10-15): el saldo que queda por cobrar (en USD el saldo va en la caja azul). */}
            {cob.moneda !== "USD" && (
              <div><p className="text-xs text-gray-400">Saldo</p><p className={`text-base font-bold ${(cob.amount || 0) - (cob.amount_paid || 0) > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="cob-detalle-saldo">{formatPYGCompact(Math.max((cob.amount || 0) - (cob.amount_paid || 0), 0))} Gs</p></div>
            )}
          </div>
          {/* Fix GL (2026-10-16): "hay que quedar registrado cuánto es lo que realmente vamos a estar cobrando
              nosotros y que quede registrado cuánto es de retención que tenemos que posteriormente cobrar" (CEN). */}
          {(Number(cob.retencion_iva || 0) > 0 || Number(cob.retencion_cobrada || 0) > 0) && (() => {
            const ret = Number(cob.retencion_iva || 0), retCob = Number(cob.retencion_cobrada || 0);
            const saldo = Math.max(Number(cob.amount || 0) - Number(cob.amount_paid || 0), 0);
            const retPend = Math.max(ret - retCob, 0);
            return (
              <div className="bg-violet-50 border border-violet-100 rounded-xl p-3 text-xs text-violet-800 space-y-1" data-testid="cob-detalle-retencion">
                <p className="font-semibold">Retención de IVA{cob.retencion_porcentaje ? ` (${cob.retencion_porcentaje} % del IVA)` : ""}: {formatPYG(ret)}</p>
                <p>A cobrar en efectivo: <span className="font-semibold">{formatPYG(Math.max(Number(cob.amount || 0) - ret, 0))}</span> · Efectivo por cobrar: <span className="font-semibold" data-testid="cob-detalle-efectivo-pend">{formatPYG(Math.max(saldo - retPend, 0))}</span></p>
                <p>Retención cobrada: <span className="font-semibold">{formatPYG(retCob)}</span> · Retención por cobrar: <span className="font-semibold" data-testid="cob-detalle-ret-pend">{formatPYG(retPend)}</span></p>
                {cob.retencion_numero && (
                  <p className="font-mono text-[11px]" data-testid="cob-detalle-ret-comprobante">Comprobante {cob.retencion_numero}{cob.retencion_fecha ? ` · ${cob.retencion_fecha}` : ""}{cob.retencion_cdc ? ` · CDC ${cob.retencion_cdc}` : ""}</p>
                )}
              </div>
            );
          })()}
          {(cob.status === "cobrado" || cob.status === "parcial") && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-emerald-700">
              <p className="font-semibold">Cobrado el {cob.payment_date}</p>
              <p className="text-xs mt-0.5">Método: {cob.payment_method} · Por: {cob.paid_by_name}</p>
            </div>
          )}
          {cob.notes && <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600 italic">"{cob.notes}"</div>}
          {comprobantes.length > 0 && (
            <div className="space-y-1.5" data-testid="cobro-comprobantes">
              <p className="text-xs font-semibold text-gray-500">Comprobantes del cobro</p>
              {comprobantes.map(cp => (
                <div key={cp.id} className={`text-xs border rounded-lg px-3 py-2 ${cp.anulado ? "border-gray-200 text-gray-400" : "border-emerald-100 text-gray-700"}`}
                  data-testid={`cobro-comprobante-${cp.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={cp.anulado ? "line-through" : ""}>{cp.tipo === "retencion_iva" ? "Retención de IVA · " : ""}N° {cp.numero} · Factura {cp.nro_factura || "—"} · {formatPYGCompact(cp.monto || 0)} Gs · {cp.fecha}</span>{/* Fix GL */}
                    {cp.anulado && <span className="text-[10px] font-semibold text-gray-500">Anulado</span>}
                    <ControlIABadge c={cp.control_ia} />{/* Fix GP */}
                    <button onClick={() => window.open(`${API}/treasury/cobranzas/${cob._id}/comprobantes/${cp.id}/file`, "_blank")}
                      className="flex items-center gap-1 font-semibold text-[#2E6DA4] hover:underline" data-testid={`cobro-comprobante-ver-${cp.id}`}>
                      <Eye size={12} /> Ver
                    </button>
                    {puedeRecibo && !cp.anulado && cp.tipo !== "retencion_iva" && (   /* Fix HU */
                      <button onClick={() => pedirRecibo(cp)} disabled={emitiendo === cp.id}
                        className="flex items-center gap-1 font-semibold text-emerald-700 hover:underline disabled:opacity-50 whitespace-nowrap" data-testid={`cobro-comprobante-recibo-${cp.id}`}>
                        <FileText size={12} /> {(cp.recibo_numero || numeros[cp.id]) ? `Recibo ${cp.recibo_numero || numeros[cp.id]}` : "Recibo"}
                      </button>
                    )}
                  </div>
                  {cp.control_ia?.estado === "con_alertas" && (   /* Fix GP: lo que se confirmó al cargar */
                    <ul className="mt-1 list-disc pl-4 text-[11px] text-amber-700" data-testid={`cobro-comprobante-alertas-${cp.id}`}>
                      {(cp.control_ia.alertas || []).map((a, i) => <li key={i}>{a}</li>)}
                      {cp.control_ia.confirmado_por && <li className="list-none -ml-4 text-gray-500">Confirmado por {cp.control_ia.confirmado_por}</li>}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          {primerRecibo && (   /* Fix HU: la primera vez se define con qué N° arranca, para no repetir los del talonario impreso */
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-900 space-y-2" data-testid="recibo-primer-numero">
              <p>Es el primer recibo que se emite desde el sistema. Indicá con qué N° arranca (si seguís el talonario impreso, el que sigue al último usado).</p>
              <label className="flex items-center gap-2 text-xs font-semibold">N° del primer recibo
                <input type="number" min="1" value={primerRecibo.valor} onChange={e => setPrimerRecibo(p => ({ ...p, valor: e.target.value }))}
                  className="w-32 border border-blue-200 rounded-lg px-2 py-1 bg-white font-mono" data-testid="recibo-primer-numero-input" />
              </label>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setPrimerRecibo(null)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-white">Cancelar</button>
                <button onClick={guardarPrimerRecibo} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="recibo-primer-numero-ok">Guardar y emitir</button>
              </div>
            </div>
          )}
          {confirmarAnular && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 space-y-2" data-testid="anular-cobro-confirmar">
              <p>La cobranza vuelve a <strong>Por Cobrar</strong>, se quita su ingreso de Finanzas y, si es de una certificación, esta vuelve a <strong>Enviada a Finanzas</strong>. El comprobante queda guardado como anulado.</p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setConfirmarAnular(false)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-white">Cancelar</button>
                <button onClick={anular} disabled={anulando} className="px-3 py-1.5 text-xs rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
                  data-testid="anular-cobro-confirm">{anulando ? "Anulando..." : "Anular cobro"}</button>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={() => downloadCobranzaPdf(cob)} data-testid="cobranza-detail-pdf-btn" className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl px-4 py-2 text-sm"><FileText size={14} /> PDF</button>
            {puedeAnular && !confirmarAnular && (
              <button onClick={() => setConfirmarAnular(true)} className="flex items-center gap-2 border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-xl px-4 py-2 text-sm"
                data-testid="anular-cobro-btn"><RotateCcw size={14} /> Anular cobro</button>
            )}
            {(cob.status === "pendiente" || cob.status === "parcial" || cob.status === "vencido") && (
              <button onClick={() => { onClose(); onMarkPaid(cob); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2 text-sm font-semibold flex items-center justify-center gap-2" data-testid="mark-paid-from-detail">
                <CheckCircle2 size={14} /> Registrar Cobro
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  , document.body);
}

// ── Main Component ────────────────────────────────────────────────────────────
const TABS_COB = [
  { key: "cobranzas", label: "Cobranzas", icon: TrendingUp },
  { key: "cxc", label: "Cuentas por Cobrar", icon: AlertTriangle },
  // Fix GZ (2026-10-19): "debe generar un estado de cuentas a partir de estas cargas" (CEN, Claudio)
  { key: "estado", label: "Estado de Cuenta", icon: FileText },
  // Fix CS (2026-09-14): "generar botones para eliminar y un basurero donde vayan los
  // eliminados" (Heber, 14/09).
  { key: "papelera", label: "Papelera", icon: Trash2 },
];

// Fix CS (2026-09-14): "también generar un filtro por cliente, fecha y tipo de operación".
const TIPOS_OPERACION = [
  ["", "Todos los tipos"], ["certificacion", "Certificación"], ["anticipo", "Anticipo"],
  ["factura", "Factura"], ["hitos", "Por hitos"], ["otros", "Otros"],
];

function PapeleraTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const cargar = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/treasury/cobranzas-papelera`, { withCredentials: true })
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(err => toast.error(err.response?.data?.detail || "No se pudo abrir la papelera"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const restaurar = async (c) => {
    try {
      await axios.put(`${API}/treasury/cobranzas/${c._id}/restaurar`, {}, { withCredentials: true });
      toast.success("Cobranza restaurada");
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo restaurar"); }
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;
  if (!rows.length) return (
    <div className="bg-white rounded-2xl border border-gray-200/60 p-16 text-center" data-testid="papelera-vacia">
      <Trash2 size={36} className="mx-auto text-gray-200 mb-3" />
      <p className="text-sm font-medium text-gray-500">La papelera está vacía</p>
    </div>
  );
  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden" data-testid="papelera-tabla">
      <table className="w-full text-sm">
        <thead><tr className="bg-gray-50/50 border-b border-gray-100 text-left text-xs uppercase text-gray-500">
          <th className="px-5 py-3 font-bold">N° COB</th><th className="px-5 py-3 font-bold">Cliente</th>
          <th className="px-5 py-3 font-bold">Proyecto</th><th className="px-5 py-3 font-bold text-right">Monto</th>
          <th className="px-5 py-3 font-bold">Eliminada</th><th className="px-5 py-3"></th>
        </tr></thead>
        <tbody>
          {rows.map(c => (
            <tr key={c._id} className="border-b border-gray-50" data-testid={`papelera-fila-${c._id}`}>
              <td className="px-5 py-3 text-xs font-mono font-bold text-gray-500">{c.number}</td>
              <td className="px-5 py-3">{c.client_name}</td>
              <td className="px-5 py-3 text-gray-500">{c.project_name || "—"}</td>
              <td className="px-5 py-3 text-right tabular-nums">{formatPYG(c.amount)}</td>
              <td className="px-5 py-3 text-xs text-gray-400">{formatDate(c.deleted_at)}{c.deleted_by_name ? ` · ${c.deleted_by_name}` : ""}</td>
              <td className="px-5 py-3 text-right">
                <button onClick={() => restaurar(c)} className="flex items-center gap-1 text-xs font-semibold text-[#2E6DA4] hover:underline ml-auto"
                  data-testid={`restaurar-${c._id}`}><RotateCcw size={12} /> Restaurar</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Modal: Registrar Cobro Parcial sobre Hito ─────────────────────────────────
function RegistrarCobroHitoModal({ cob, hito, onClose, onSaved }) {
  const pendiente = (hito.monto_esperado || 0) - (hito.monto_cobrado || 0);
  const [form, setForm] = useState({
    monto: String(pendiente),
    fecha: new Date().toISOString().slice(0, 10),
    payment_method: "transferencia",
    notes: "",
  });
  const [datos, setDatos] = useState(() => datosCobroIniciales(cob));
  const [saving, setSaving] = useState(false);
  // Fix GP (2026-10-16): la IA lee el comprobante bancario del hito y completa monto, N° y fecha.
  const ia = useLecturaIA(cob, { hitoId: hito.id, aplicar: (a) => {
    setForm(p => ({ ...p,
      ...(a.monto ? { monto: String(Math.round(Number(a.monto))) } : {}),
      ...(a.fecha ? { fecha: a.fecha } : {}),
      ...(a.metodo ? { payment_method: a.metodo } : {}),
    }));
    if (a.numero) setDatos(p => ({ ...p, comprobante_numero: a.numero }));
  } });

  const submit = async () => {
    const m = parseFloat(form.monto || 0);
    if (m <= 0) { toast.error("Monto debe ser > 0"); return; }
    if (m > pendiente + 0.01) { toast.error(`El cobro excede el pendiente del hito (Gs. ${Math.round(pendiente).toLocaleString()})`); return; }
    const falta = faltanDatosCobro(datos);
    if (falta) { toast.error(falta); return; }
    if (ia.bloquea) { toast.error(ia.leyendo ? "Esperá a que termine la lectura del comprobante" : "Revisá y confirmá las alertas del comprobante"); return; }   // Fix GP
    setSaving(true);
    try {
      await axios.put(`${API}/treasury/cobranzas/${cob._id}/hitos/${hito.id}/registrar`, {
        monto: m, fecha: form.fecha, payment_method: form.payment_method, notes: form.notes, ...datos,
        ...ia.payload(),   // Fix GP
      }, { withCredentials: true });
      toast.success(m >= pendiente - 0.01 ? "Hito cobrado completo" : "Cobro parcial registrado");
      onSaved();
      onClose();
    } catch (err) {
      if (!ia.errorAlConfirmar(err)) toast.error(err.response?.data?.detail || "Error al registrar cobro");   // Fix GP
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Registrar Cobro de Hito</h2>
            <p className="text-xs text-gray-500 mt-0.5">{cob.number} · {hito.description}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700 space-y-0.5">
            <div className="flex justify-between"><span>Esperado del hito:</span> <span className="font-bold">Gs. {Math.round(hito.monto_esperado).toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Ya cobrado:</span> <span>Gs. {Math.round(hito.monto_cobrado || 0).toLocaleString()}</span></div>
            <div className="flex justify-between text-emerald-700"><span>Pendiente:</span> <span className="font-bold">Gs. {Math.round(pendiente).toLocaleString()}</span></div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Monto a cobrar *</label>
            <input type="number" value={form.monto} onChange={e => setForm(p => ({ ...p, monto: e.target.value }))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="hito-monto-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha</label>
            <input type="date" value={form.fecha} onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Método de Pago</label>
            <select value={form.payment_method} onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white">
              {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" placeholder="N° transferencia, cheque..." />
          </div>
          <CamposComprobanteCobro datos={datos} setDatos={setDatos} onArchivo={ia.onArchivo}>
            <PanelLecturaIA ia={ia} />{/* Fix GP */}
          </CamposComprobanteCobro>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={submit} disabled={saving || !!faltanDatosCobro(datos) || ia.bloquea} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="confirm-hito-cobro-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {saving ? "Guardando..." : "Confirmar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}

// ── Modal: Ver/Agregar Hitos de una cobranza ─────────────────────────────────
function HitosModal({ cob, onClose, onSaved }) {
  const [hitos, setHitos] = useState(cob.hitos || []);
  const [showAdd, setShowAdd] = useState(false);
  const [newHito, setNewHito] = useState({ description: "", monto_esperado: "", fecha_prevista: "" });
  const [registrar, setRegistrar] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/treasury/cobranzas/${cob._id}/hitos`, { withCredentials: true });
      setHitos(res.data.hitos || []);
      onSaved && onSaved();
    } catch { /* noop */ }
  };

  const addHito = async () => {
    if (!newHito.description || !newHito.monto_esperado) {
      toast.error("Descripción y monto esperado son requeridos");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/treasury/cobranzas/${cob._id}/hitos`, {
        description: newHito.description,
        monto_esperado: Number(newHito.monto_esperado),
        fecha_prevista: newHito.fecha_prevista,
      }, { withCredentials: true });
      toast.success("Hito agregado");
      setNewHito({ description: "", monto_esperado: "", fecha_prevista: "" });
      setShowAdd(false);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agregar hito");
    } finally {
      setSaving(false);
    }
  };

  const totalEsperado = hitos.reduce((s, h) => s + (h.monto_esperado || 0), 0);
  const totalCobrado = hitos.reduce((s, h) => s + (h.monto_cobrado || 0), 0);
  const restanteCobranza = Math.max(cob.monto_total - totalEsperado, 0);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Hitos de Cobro</h2>
            <p className="text-xs text-gray-500 mt-0.5">{cob.number} · {cob.client_name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase">Monto Total</p><p className="text-sm font-bold text-gray-900">Gs. {Math.round(cob.monto_total).toLocaleString()}</p></div>
            <div className="bg-emerald-50 rounded-xl p-3"><p className="text-[10px] text-emerald-600 uppercase">Cobrado</p><p className="text-sm font-bold text-emerald-700">Gs. {Math.round(totalCobrado).toLocaleString()}</p></div>
            <div className="bg-amber-50 rounded-xl p-3"><p className="text-[10px] text-amber-600 uppercase">Pendiente</p><p className="text-sm font-bold text-amber-700">Gs. {Math.round(cob.monto_total - totalCobrado).toLocaleString()}</p></div>
          </div>

          {hitos.length === 0 && !showAdd && (
            <div className="text-center text-gray-400 py-8">
              <Calendar size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Esta cobranza no tiene hitos definidos.</p>
              <p className="text-xs mt-1">Agregá hitos para cobrar en múltiples etapas (ej: 50% / 30% / 20%).</p>
            </div>
          )}

          {hitos.length > 0 && (
            <div className="space-y-2">
              {hitos.map((h, idx) => {
                const pend = (h.monto_esperado || 0) - (h.monto_cobrado || 0);
                const completo = pend <= 0.01;
                return (
                  <div key={h.id || idx} className={`rounded-xl border p-3 ${completo ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-gray-200"}`} data-testid={`hito-row-${idx}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{h.description}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Esperado: Gs. {Math.round(h.monto_esperado).toLocaleString()} · Cobrado: Gs. {Math.round(h.monto_cobrado || 0).toLocaleString()}
                          {h.fecha_prevista && <span> · Prev: {h.fecha_prevista}</span>}
                          {h.fecha_real && <span className="text-emerald-600"> · Cobrado el {h.fecha_real}</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${completo ? "bg-emerald-100 text-emerald-700" : (h.monto_cobrado > 0 ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700")}`}>
                          {completo ? "Cobrado" : (h.monto_cobrado > 0 ? "Parcial" : "Pendiente")}
                        </span>
                        {!completo && (
                          <button onClick={() => setRegistrar(h)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1" data-testid={`registrar-cobro-hito-${idx}`}>
                            <DollarSign size={11} /> Registrar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {showAdd ? (
            <div className="bg-blue-50 rounded-xl p-4 space-y-3 border border-blue-100">
              <p className="text-sm font-semibold text-gray-800">Nuevo Hito</p>
              <input value={newHito.description} onChange={e => setNewHito(p => ({ ...p, description: e.target.value }))} placeholder="Ej: 50% al firmar contrato" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="new-hito-description" />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={newHito.monto_esperado} onChange={e => setNewHito(p => ({ ...p, monto_esperado: e.target.value }))} placeholder="Monto esperado" className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="new-hito-monto" />
                <input type="date" value={newHito.fecha_prevista} onChange={e => setNewHito(p => ({ ...p, fecha_prevista: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" />
              </div>
              <p className="text-[11px] text-gray-500">Disponible para asignar: Gs. {Math.round(restanteCobranza).toLocaleString()}</p>
              <div className="flex gap-2">
                <button onClick={() => setShowAdd(false)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg py-2 text-xs">Cancelar</button>
                <button onClick={addHito} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-xs font-semibold disabled:opacity-60" data-testid="confirm-add-hito">
                  {saving ? "..." : "Agregar"}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)} className="w-full border border-dashed border-blue-300 text-blue-700 hover:bg-blue-50 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2" data-testid="open-add-hito-btn">
              <Plus size={14} /> Agregar Hito
            </button>
          )}
        </div>
      </div>
      {registrar && <RegistrarCobroHitoModal cob={cob} hito={registrar} onClose={() => setRegistrar(null)} onSaved={refresh} />}
    </div>
  , document.body);
}

// ── Tab Cuentas por Cobrar ────────────────────────────────────────────────────
function CuentasPorCobrarTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("todos");
  const [search, setSearch] = useState("");
  const [openHitos, setOpenHitos] = useState(null);
  // Fix EB (2026-09-23): filtro por fecha de emisión, desde / hasta, como en los libros.
  const [cxcDesde, setCxcDesde] = useState("");
  const [cxcHasta, setCxcHasta] = useState("");
  const [exportando, setExportando] = useState("");
  const [estadoCli, setEstadoCli] = useState(null);   // Fix FO (2026-10-09): cuenta corriente del cliente

  const fetchAR = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/treasury/accounts-receivable`, { withCredentials: true });
      setRows(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar cuentas por cobrar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAR(); }, []);

  const filtered = rows.filter(r => {
    const matchSearch = !search || r.client_name?.toLowerCase().includes(search.toLowerCase()) || r.number?.toLowerCase().includes(search.toLowerCase()) || r.invoice_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "todos" || r.status === filterStatus;
    const emision = String(r.issue_date || "").slice(0, 10);
    const matchDesde = !cxcDesde || (emision && emision >= cxcDesde);
    const matchHasta = !cxcHasta || (emision && emision <= cxcHasta);
    return matchSearch && matchStatus && matchDesde && matchHasta;
  });

  const exportarCxc = (tipo) => exportarReporte(tipo, {
    titulo: "Cuentas por Cobrar",
    filtros: [cxcDesde && `Emisión desde ${fechaReporte(cxcDesde)}`, cxcHasta && `hasta ${fechaReporte(cxcHasta)}`,
      filterStatus !== "todos" && `Estado ${filterStatus}`, search && `Búsqueda "${search}"`].filter(Boolean).join(" · "),
    columnas: [{ l: "Cliente" }, { l: "Obra" }, { l: "N° Factura" }, { l: "N° COB" }, { l: "Emisión" }, { l: "Vencimiento" },
      { l: "Total (Gs)", num: true }, { l: "Cobrado (Gs)", num: true }, { l: "Pendiente (Gs)", num: true }, { l: "Días" }, { l: "Estado" },
      { l: "Moneda" }, { l: "Total USD", num: true }, { l: "Pendiente USD", num: true }],   // Fix FG
    // Fix FR (2026-10-12): "sumemos el total de una misma moneda nada más… PYG deben sumarse solo con PYG
    // y USD con USD" (HEGS, 12/10). Las facturas en USD van solo en sus columnas en USD, y los totales
    // en Gs. suman solo las facturas en Gs.
    filas: filtered.map(r => [r.client_name, r.project_name || "", r.invoice_number || "Sin factura", r.number, fechaReporte(r.issue_date),
      fechaReporte(r.due_date), r.moneda === "USD" ? "" : r.monto_total || 0, r.moneda === "USD" ? "" : r.monto_cobrado || 0,
      r.moneda === "USD" ? "" : r.monto_pendiente || 0, r.dias_emision, r.status,
      r.moneda === "USD" ? "USD" : "PYG", r.moneda === "USD" ? r.monto_usd || 0 : "", r.moneda === "USD" ? r.pendiente_usd || 0 : ""]),
    totales: ["TOTALES", "", "", "", "", "", filtered.reduce((a, r) => a + (r.moneda === "USD" ? 0 : r.monto_total || 0), 0),
      filtered.reduce((a, r) => a + (r.moneda === "USD" ? 0 : r.monto_cobrado || 0), 0),
      filtered.reduce((a, r) => a + (r.moneda === "USD" ? 0 : r.monto_pendiente || 0), 0), "", "", "",
      filtered.reduce((a, r) => a + (r.moneda === "USD" ? r.monto_usd || 0 : 0), 0),
      filtered.reduce((a, r) => a + (r.moneda === "USD" ? r.pendiente_usd || 0 : 0), 0)],
    archivo: `Cuentas_por_Cobrar_${new Date().toISOString().slice(0, 10)}`,
  }, setExportando);

  // Fix FR (2026-10-12): las tarjetas también suman cada moneda por separado.
  const totalPendiente = rows.filter(r => r.status !== "cobrada" && r.moneda !== "USD").reduce((a, r) => a + r.monto_pendiente, 0);
  const totalVencido = rows.filter(r => r.status === "vencida" && r.moneda !== "USD").reduce((a, r) => a + r.monto_pendiente, 0);
  const totalPendienteUsd = rows.filter(r => r.status !== "cobrada" && r.moneda === "USD").reduce((a, r) => a + (r.pendiente_usd || 0), 0);
  const totalVencidoUsd = rows.filter(r => r.status === "vencida" && r.moneda === "USD").reduce((a, r) => a + (r.pendiente_usd || 0), 0);
  const cntVencidas = rows.filter(r => r.status === "vencida").length;
  const cntParciales = rows.filter(r => r.status === "parcial").length;

  const statusColor = {
    vencida: "bg-red-100 text-red-700 border-red-200",
    parcial: "bg-blue-100 text-blue-700 border-blue-200",
    pendiente: "bg-amber-100 text-amber-700 border-amber-200",
    cobrada: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  return (
    <div className="space-y-5" data-testid="cxc-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Pendiente" value={formatPYGCompact(totalPendiente)} sub={totalPendienteUsd > 0 ? `A cobrar · + ${formatUSD2(totalPendienteUsd)}` : "A cobrar"} color="amber" icon={Clock} />
        <StatCard title="Vencidas" value={cntVencidas} sub={`Gs. ${formatPYGCompact(totalVencido)}${totalVencidoUsd > 0 ? ` · + ${formatUSD2(totalVencidoUsd)}` : ""}`} color="red" icon={AlertTriangle} />
        <StatCard title="Cobros Parciales" value={cntParciales} sub="En curso" color="blue" icon={DollarSign} />
        <StatCard title="Total Cuentas" value={rows.length} sub="Activas" color="emerald" icon={CheckCircle2} />
      </div>

      {/* Fix EW (2026-10-02): el buscador conserva un ancho mínimo; lo que no entra baja de renglón. */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
        <div className="relative flex-1 max-w-sm sm:min-w-[160px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-emerald-200" placeholder="Buscar cliente / factura..." value={search} onChange={e => setSearch(e.target.value)} data-testid="cxc-search" />
        </div>
        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500" title="Fecha de emisión">Desde
          <input type="date" value={cxcDesde} onChange={e => setCxcDesde(e.target.value)}
            className="border border-gray-200 rounded-xl px-2 py-1.5 text-xs bg-white" data-testid="cxc-desde" />
        </label>
        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500" title="Fecha de emisión">Hasta
          <input type="date" value={cxcHasta} onChange={e => setCxcHasta(e.target.value)}
            className="border border-gray-200 rounded-xl px-2 py-1.5 text-xs bg-white" data-testid="cxc-hasta" />
        </label>
        <div className="flex gap-2 flex-wrap">
          {["todos", "vencida", "parcial", "pendiente", "cobrada"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all ${filterStatus === s ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`} data-testid={`cxc-filter-${s}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <button onClick={fetchAR} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50"><RefreshCw size={14} className="text-gray-500" /></button>
        <BotonesExportar exportar={exportarCxc} exportando={exportando} testId="cxc-exportar" />
        {/* Fix FO (2026-10-09): "necesitamos cuenta corriente de los clientes" (CEN, reunión de Finanzas) */}
        <button onClick={() => setEstadoCli("")}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-[#2E6DA4]/40 text-[#2E6DA4] hover:bg-blue-50"
          data-testid="cxc-estado-cuenta-btn"><FileText size={13} /> Estado de cuenta</button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Obra</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">N° / Factura</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Emisión</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Total</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Cobrado</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Pendiente</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Días</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 10 }).map((__, j) => <td key={j} className="px-4 py-4"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-gray-400 text-sm"><AlertTriangle size={32} className="mx-auto mb-2 opacity-30" />No hay cuentas por cobrar</td></tr>
              ) : (
                filtered.map(r => (
                  <tr key={r._id} className={`border-b border-gray-50 transition-colors hover:bg-gray-50/50 ${r.status === "vencida" ? "bg-red-50/40" : r.status === "parcial" ? "bg-blue-50/30" : ""}`} data-testid={`cxc-row-${r._id}`}>
                    <td className="px-4 py-3.5"><p className="text-sm font-semibold text-gray-900">{r.client_name}</p><p className="text-[11px] text-gray-400 truncate max-w-[180px]">{r.description}</p></td>
                    <td className="px-4 py-3.5 text-xs text-gray-500">{r.project_name || "—"}</td>
                    {/* Fix EA (2026-09-23): "Aqui debe aparecer el numero verdadero de la factura cargada
                        previamente, NO el numero interno de referencia de la cobranza". */}
                    <td className="px-4 py-3.5">
                      {r.invoice_number
                        ? <p className="text-xs font-mono text-emerald-700 font-bold" data-testid={`cxc-factura-${r._id}`}>{r.invoice_number}</p>
                        : <p className="text-xs font-semibold text-amber-600" data-testid={`cxc-factura-${r._id}`}>Sin factura</p>}
                      <p className="text-[11px] font-mono text-gray-400">{r.number}</p>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-gray-500">{r.issue_date || "—"}</td>
                    {/* Fix FG (2026-10-08): en USD, con su equivalente en Gs. (lo pendiente al cambio de hoy). */}
                    <td className="px-4 py-3.5 text-right text-sm font-bold text-gray-900">{formatPYGCompact(r.monto_total)}{r.moneda === "USD" && <p className="text-[11px] font-semibold text-blue-600">{formatUSD2(r.monto_usd)}</p>}</td>
                    <td className="px-4 py-3.5 text-right text-xs text-emerald-600">{formatPYGCompact(r.monto_cobrado)}{r.moneda === "USD" && <p className="text-[11px] text-blue-600">{formatUSD2(r.cobrado_usd)}</p>}</td>
                    <td className="px-4 py-3.5 text-right text-sm font-bold text-amber-700" data-testid={`cxc-pendiente-${r._id}`}>{formatPYGCompact(r.monto_pendiente)}{r.moneda === "USD" && <p className="text-[11px] font-semibold text-blue-600">{formatUSD2(r.pendiente_usd)}</p>}</td>
                    <td className="px-4 py-3.5 text-center text-xs"><span className={r.dias_emision > 60 ? "text-red-600 font-bold" : "text-gray-500"}>{r.dias_emision}d</span></td>
                    <td className="px-4 py-3.5 text-center">
                      <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-semibold border ${statusColor[r.status] || ""}`}>
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      {r.moneda !== "USD" && (   /* Fix FG: los hitos son para cobranzas en Gs. */
                      <button onClick={() => setOpenHitos(r)} className="text-emerald-600 hover:bg-emerald-50 px-2 py-1 rounded-lg text-xs font-semibold flex items-center gap-1" data-testid={`view-hitos-${r._id}`}>
                        <Eye size={12} /> Hitos
                      </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openHitos && <HitosModal cob={openHitos} onClose={() => setOpenHitos(null)} onSaved={fetchAR} />}
      {estadoCli !== null && <EstadoCuentaModal tipo="cliente" nombre={estadoCli} onClose={() => setEstadoCli(null)} testId="cxc-estado"
        opciones={[...new Set(rows.map(r => r.client_name).filter(Boolean))].sort()} />}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function TesoreriaCobranzas() {
  const [activeTab, setActiveTab] = useState("cobranzas");
  const [cobranzas, setCobranzas] = useState([]);
  const [stats, setStats]         = useState({});
  const [projects, setProjects]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterProject, setFilterProject] = useState("");
  // Fix CS (2026-09-14): filtros por cliente, fecha y tipo de operación.
  const [filterCliente, setFilterCliente] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterDesde, setFilterDesde] = useState("");
  const [filterHasta, setFilterHasta] = useState("");
  const [borrarCob, setBorrarCob] = useState(null);
  const [showNew, setShowNew]     = useState(false);
  const [editCob, setEditCob]     = useState(null);   // Fix GY: modificar factura
  const [markPaid, setMarkPaid]   = useState(null);
  const [detailCob, setDetailCob] = useState(null);
  const [exportando, setExportando] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cobRes, statsRes, projRes] = await Promise.all([
        axios.get(`${API}/treasury/cobranzas`, { withCredentials: true }),
        axios.get(`${API}/treasury/cobranzas/stats`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
      ]);
      setCobranzas(cobRes.data);
      setStats(statsRes.data);
      setProjects(projRes.data);
    } catch {
      toast.error("Error al cargar cobranzas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = cobranzas.filter(c => {
    const matchSearch = !search || c.client_name?.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase()) || c.number?.toLowerCase().includes(search.toLowerCase()) || c.invoice_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "todos" || (filterStatus === "anulada" ? !!c.anulada : c.status === filterStatus);   // Fix HO
    const matchProject = !filterProject || c.project_id === filterProject;
    // Fix CS (2026-09-14): cliente, tipo de operación y rango de fechas.
    const matchCliente = !filterCliente || c.client_name === filterCliente;
    const matchTipo = !filterTipo || (c.tipo || c.origen || (c.hitos?.length ? "hitos" : "otros")) === filterTipo;
    const fecha = String(c.payment_date || c.due_date || c.created_at || "").slice(0, 10);
    const matchDesde = !filterDesde || (fecha && fecha >= filterDesde);
    const matchHasta = !filterHasta || (fecha && fecha <= filterHasta);
    return matchSearch && matchStatus && matchProject && matchCliente && matchTipo && matchDesde && matchHasta;
  }).sort(porNumeroDesc);   // Fix HS
  const clientesCob = [...new Set(cobranzas.map(c => c.client_name).filter(Boolean))].sort();

  // Fix EB (2026-09-23): Excel y PDF de las cobranzas filtradas.
  const exportarCobranzas = (tipo) => exportarReporte(tipo, {
    titulo: "Cobranzas",
    filtros: [filterDesde && `Desde ${fechaReporte(filterDesde)}`, filterHasta && `Hasta ${fechaReporte(filterHasta)}`,
      filterCliente && `Cliente ${filterCliente}`,
      filterProject && `Proyecto ${projects.find(p => p._id === filterProject)?.name || ""}`,
      filterTipo && `Tipo ${(TIPOS_OPERACION.find(t => t[0] === filterTipo) || [])[1] || filterTipo}`,
      filterStatus !== "todos" && `Estado ${STATUS_MAP[filterStatus]?.label || filterStatus}`,
      search && `Búsqueda "${search}"`].filter(Boolean).join(" · "),
    columnas: [{ l: "N° COB" }, { l: "Cliente" }, { l: "Proyecto" }, { l: "Descripción" }, { l: "N° Factura" }, { l: "Emisión" },
      { l: "Vencimiento" }, { l: "Fecha de cobro" }, { l: "Monto (Gs)", num: true }, { l: "Cobrado (Gs)", num: true }, { l: "Estado" },
      { l: "Moneda" }, { l: "Monto USD", num: true }, { l: "Cobrado USD", num: true }],   // Fix FG
    filas: filtered.map(c => [c.number, c.client_name, c.project_name || "", c.description || "", c.nro_factura || c.invoice_number || "",
      fechaReporte(c.issue_date), fechaReporte(c.due_date), fechaReporte(c.payment_date),
      c.moneda === "USD" ? "" : c.amount || 0, c.moneda === "USD" ? "" : c.amount_paid || 0,   // Fix FR (2026-10-12): cada moneda en sus columnas
      c.anulada ? "Anulada" : STATUS_MAP[c.status]?.label || c.status,   // Fix HO
      c.moneda === "USD" ? "USD" : "PYG", c.moneda === "USD" ? c.monto_usd || 0 : "", c.moneda === "USD" ? c.monto_usd_cobrado || 0 : ""]),
    totales: ["TOTALES", "", "", "", "", "", "", "", filtered.reduce((a, c) => a + (c.moneda === "USD" ? 0 : c.amount || 0), 0),
      filtered.reduce((a, c) => a + (c.moneda === "USD" ? 0 : c.amount_paid || 0), 0), "", "",
      filtered.reduce((a, c) => a + (c.moneda === "USD" ? c.monto_usd || 0 : 0), 0),
      filtered.reduce((a, c) => a + (c.moneda === "USD" ? c.monto_usd_cobrado || 0 : 0), 0)],
    archivo: `Cobranzas_${new Date().toISOString().slice(0, 10)}`,
  }, setExportando);

  const borrar = async () => {
    if (!borrarCob) return;
    try {
      await axios.delete(`${API}/treasury/cobranzas/${borrarCob._id}`, { withCredentials: true });
      toast.success("Cobranza enviada a la papelera");
      setBorrarCob(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Tesorería</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Facturación y Cobranza</h1>   {/* Fix GY (2026-10-19): "en esta sección debe ser Facturación y Cobranza" (CEN, Claudio) */}
          <p className="text-sm text-gray-500 mt-0.5">Facturación y seguimiento de cobros a clientes por proyecto</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchAll} className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all" title="Actualizar"><RefreshCw size={15} className="text-gray-500" /></button>
          <button onClick={() => setShowNew(true)} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm" data-testid="new-cobranza-btn">
            <Plus size={15} /> Nueva Factura
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1" data-testid="cobranzas-tabs">
          {TABS_COB.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${activeTab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "cxc" && <CuentasPorCobrarTab />}
      {activeTab === "estado" && <EstadoCuentaTab />}
      {activeTab === "papelera" && <PapeleraTab />}
      {activeTab === "cobranzas" && (
      <>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Por Cobrar" value={formatPYGCompact(stats.monto_pendiente || 0)} sub={`${stats.pendientes || 0} pendientes${stats.usd?.pendiente ? ` · incluye ${formatUSD2(stats.usd.pendiente)}` : ""}`} color="amber" icon={Clock} />
        <StatCard title="Cobrado" value={formatPYGCompact(stats.monto_cobrado || 0)} sub={`${stats.cobrados || 0} cobrados · ${stats.eficiencia_pct || 0}%${stats.usd?.cobrado ? ` · incluye ${formatUSD2(stats.usd.cobrado)}` : ""}`} color="emerald" icon={CheckCircle2} />
        <StatCard title="Parcial" value={formatPYGCompact(stats.monto_parcial || 0)} sub={`${stats.parciales || 0} cobros parciales`} color="blue" icon={DollarSign} />
        <StatCard title="Vencidos" value={stats.vencidos || 0} sub="Cobros atrasados" color="red" icon={AlertTriangle} />
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700">Eficiencia de Cobranza</p>
          <span className="text-sm font-bold text-emerald-600">{stats.eficiencia_pct || 0}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5">
          <div className="bg-emerald-500 h-2.5 rounded-full transition-all duration-700" style={{ width: `${stats.eficiencia_pct || 0}%` }} />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>Cobrado: {formatPYGCompact(stats.monto_cobrado || 0)} Gs</span>
          {stats.usd?.cantidad > 0 && <span data-testid="cob-tc-hoy">USD: lo cobrado al cambio de cada cobro; lo pendiente a {fmtTC(stats.tc_hoy)}</span>}
          <span>Total facturado: {formatPYGCompact(stats.total || 0)} Gs</span>
        </div>
      </div>

      {/* Filters */}
      {/* Fix EU (2026-10-01): los filtros bajan de renglón cuando no entran */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
        <div className="relative flex-1 sm:min-w-[220px] max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all" placeholder="Buscar cliente, N° factura..." value={search} onChange={e => setSearch(e.target.value)} data-testid="cobranzas-search" />
        </div>
        {/* Fix HG (2026-10-20): obras de la A a la Z y con buscador (se puede escribir el nombre). */}
        <SelectBuscable value={filterProject} onChange={setFilterProject}
          options={opcionesObrasPpto(projects)} vacio="Todos los proyectos" placeholderBuscar="Buscar obra, cliente o N° de presupuesto..."   // Fix HR
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-200 sm:min-w-[200px] sm:max-w-xs" testId="cob-filtro-proyecto" />
        {/* Fix CS (2026-09-14): cliente, tipo de operación y rango de fechas. */}
        <select value={filterCliente} onChange={e => setFilterCliente(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-200" data-testid="cob-filtro-cliente">
          <option value="">Todos los clientes</option>
          {clientesCob.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-200" data-testid="cob-filtro-tipo">
          {TIPOS_OPERACION.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {/* Fix EB (2026-09-23): "habilitar filtros por fecha desde hasta, igual que el libro de
            ventas/compras". Los campos existían sin nombre; el criterio es el de siempre. */}
        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500" title="Fecha de cobro; si no se cobró, la de vencimiento">Desde
          <input type="date" value={filterDesde} onChange={e => setFilterDesde(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white" data-testid="cob-filtro-desde" />
        </label>
        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500" title="Fecha de cobro; si no se cobró, la de vencimiento">Hasta
          <input type="date" value={filterHasta} onChange={e => setFilterHasta(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white" data-testid="cob-filtro-hasta" />
        </label>
        {(filterCliente || filterTipo || filterDesde || filterHasta) && (
          <button onClick={() => { setFilterCliente(""); setFilterTipo(""); setFilterDesde(""); setFilterHasta(""); }}
            className="text-xs font-semibold text-gray-500 hover:text-gray-700 underline self-center" data-testid="cob-limpiar-filtros">
            Limpiar filtros
          </button>
        )}
        <div className="flex gap-2 flex-wrap">
          {["todos", "pendiente", "cobrado", "parcial", "vencido", "anulada"].map(s => (   // Fix HO: + anuladas
            <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all ${filterStatus === s ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}>
              {s === "todos" ? "Todos" : STATUS_MAP[s]?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500" data-testid="cob-cantidad">{filtered.length} cobranza{filtered.length === 1 ? "" : "s"} con estos filtros</p>
        <BotonesExportar exportar={exportarCobranzas} exportando={exportando} testId="cob-exportar" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">N° COB</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Cliente</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Proyecto</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">N° Factura</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Vcto.</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Monto</th>
                <th className="text-center px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="px-5 py-4"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-gray-400">
                    <TrendingUp size={36} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium">No hay facturas registradas</p>
                    <p className="text-xs mt-1">Haga clic en «Nueva Factura» para registrar una factura a cliente</p>
                  </td>
                </tr>
              ) : (
                filtered.map((c, i) => {
                  const st = STATUS_MAP[c.anulada ? "anulada" : c.status] || { label: c.status, cls: "" };   // Fix HO
                  const isOverdue = c.status === "vencido";
                  return (
                    <tr key={c._id || i} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${isOverdue ? "bg-red-50/30" : ""}`}>
                      <td className="px-5 py-3.5 text-xs font-mono font-bold text-emerald-700">{c.number}</td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-gray-900">{c.client_name}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[180px]">{c.description}</p>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">{c.project_name || "—"}</td>
                      <td className="px-5 py-3.5 text-xs font-mono text-gray-500">{c.nro_factura || c.invoice_number || "—"}</td>
                      <td className="px-5 py-3.5">
                        {c.due_date ? (
                          <div className={`flex items-center gap-1 text-xs ${isOverdue ? "text-red-600 font-semibold" : "text-gray-500"}`}>
                            {isOverdue && <AlertCircle size={11} />}
                            {c.due_date}
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        {c.anulada ? <span className="text-gray-300" data-testid={`cob-anulada-monto-${c._id}`}>—</span> : c.moneda === "USD" ? (<>
                          {/* Fix FG (2026-10-08): en USD, con su equivalente en Gs. */}
                          <p className="text-sm font-bold text-blue-700" data-testid={`cob-monto-usd-${c._id}`}>{formatUSD2(c.monto_usd)}</p>
                          <p className="text-[11px] text-gray-400">≈ {formatPYGCompact(c.status === "cobrado" ? c.amount_paid : Math.round((c.monto_usd - (c.monto_usd_cobrado || 0)) * (stats.tc_hoy || c.tipo_cambio)) + (c.amount_paid || 0))} Gs</p>
                          {c.monto_usd_cobrado > 0 && c.monto_usd_cobrado < c.monto_usd && (
                            <p className="text-xs text-blue-500">Cobrado: {formatUSD2(c.monto_usd_cobrado)}</p>
                          )}
                        </>) : (<>
                          <p className="text-sm font-bold text-emerald-700">{formatPYGCompact(c.amount)} Gs</p>
                          {c.amount_paid > 0 && c.amount_paid < c.amount && (
                            <p className="text-xs text-blue-500">Cobrado: {formatPYGCompact(c.amount_paid)} Gs</p>
                          )}
                        </>)}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex items-center text-xs px-2.5 py-0.5 rounded-full font-semibold border ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setDetailCob(c)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors" title="Ver detalle" data-testid={`view-cob-${c._id}`}><Eye size={14} /></button>
                          {c.status !== "cancelado" && (
                            <button onClick={() => setEditCob(c)} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors" title="Modificar factura" data-testid={`edit-cob-${c._id}`}><Pencil size={14} /></button>
                          )}
                          {!c.anulada && (
                            <button onClick={() => downloadCobranzaPdf(c)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors" title="PDF" data-testid={`pdf-cobranza-${c._id}`}><FileText size={14} /></button>
                          )}
                          {(c.status === "pendiente" || c.status === "parcial" || c.status === "vencido") && (
                            <button onClick={() => setMarkPaid(c)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors" title="Registrar cobro" data-testid={`mark-paid-${c._id}`}><CheckCircle2 size={14} /></button>
                          )}
                          {/* Fix CS (2026-09-14): a la papelera, no borrado definitivo. */}
                          <button onClick={() => setBorrarCob(c)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-600 transition-colors" title="Eliminar" data-testid={`delete-cob-${c._id}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Modals */}
      {showNew && <NuevaFacturaModal projects={projects} onClose={() => setShowNew(false)} onSaved={fetchAll} />}
      {editCob && <NuevaFacturaModal cob={editCob} projects={projects} onClose={() => setEditCob(null)} onSaved={fetchAll} />}
      {markPaid && <MarkPaidModal cob={markPaid} onClose={() => setMarkPaid(null)} onSaved={fetchAll} />}
      {detailCob && <DetailModal cob={detailCob} onClose={() => setDetailCob(null)} onMarkPaid={(c) => { setMarkPaid(c); }} onSaved={fetchAll} />}
      {borrarCob && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="confirmar-borrar-cob">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setBorrarCob(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <p className="font-bold text-gray-900">Eliminar cobranza</p>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">{borrarCob.number}</span> — {borrarCob.client_name} por {formatPYG(borrarCob.amount)} va a la papelera.
              Si tenía un cobro asentado en Finanzas, el asiento también se quita. Se puede restaurar desde la pestaña Papelera.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setBorrarCob(null)} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
              <button onClick={borrar} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2" data-testid="confirmar-borrar-btn">
                <Trash2 size={15} /> Eliminar
              </button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
